import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { Sparkles, ArrowLeft } from "lucide-react";

export default function AdminLogin() {
  const { user, login, error } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ email: "", password: "" });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (user && user.role === "admin") {
      navigate("/admin/dashboard", { replace: true });
    }
  }, [user, navigate]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    const ok = await login(form.email, form.password);
    setSubmitting(false);
    if (ok) navigate("/admin/dashboard");
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#0A0A0A] relative overflow-hidden" data-testid="admin-login-page">
      <div className="pink-glow-blob" style={{ width: 500, height: 500, background: "rgba(255,32,121,0.25)", top: "-100px", right: "-100px" }} />
      <div className="pink-glow-blob" style={{ width: 400, height: 400, background: "rgba(255,32,121,0.15)", bottom: "-80px", left: "-80px", animationDelay: "2s" }} />

      <div className="relative w-full max-w-md mx-6">
        <Link to="/" data-testid="back-home-link" className="inline-flex items-center gap-2 text-[10px] tracking-[0.3em] uppercase text-[#A09CA3] hover:text-white mb-8">
          <ArrowLeft className="w-3 h-3" /> Back to studio
        </Link>

        <div className="glass-card rounded-sm p-8 md:p-10">
          <div className="flex items-center gap-2 mb-2">
            <Sparkles className="w-5 h-5 text-[#FF2079]" />
            <span className="font-display text-2xl text-white">
              Pink<span className="text-[#FF2079]">Glow</span>
            </span>
          </div>
          <div className="eyebrow mt-6">Admin Console</div>
          <h1 className="font-display text-3xl text-white mt-2">Welcome back.</h1>
          <p className="text-sm text-[#A09CA3] mt-2">
            Sign in to manage reservations and studio operations.
          </p>

          <form onSubmit={onSubmit} className="mt-8 space-y-4">
            <div className="space-y-1.5">
              <Label className="text-[10px] tracking-[0.25em] uppercase text-[#A09CA3]">Email</Label>
              <Input
                data-testid="admin-email-input"
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                required
                className="bg-[#141414] border-white/10 text-white focus:border-[#FF2079] focus-visible:ring-0 rounded-sm h-11"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-[10px] tracking-[0.25em] uppercase text-[#A09CA3]">Password</Label>
              <Input
                data-testid="admin-password-input"
                type="password"
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                required
                className="bg-[#141414] border-white/10 text-white focus:border-[#FF2079] focus-visible:ring-0 rounded-sm h-11"
              />
            </div>

            {error && (
              <div data-testid="admin-login-error" className="text-xs text-[#FF2079] bg-[#FF2079]/10 border border-[#FF2079]/30 rounded-sm p-3">
                {error}
              </div>
            )}

            <Button
              type="submit"
              disabled={submitting}
              data-testid="admin-login-submit-btn"
              className="pink-glow-btn w-full bg-[#FF2079] hover:bg-[#D91666] text-white rounded-sm h-12 text-[11px] tracking-[0.28em] uppercase font-medium disabled:opacity-60"
            >
              {submitting ? "Signing in…" : "Sign In"}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
